import Typography from '@/components/Typography';
import { MasterTranslation, TextStyles } from '@/constants';
import { useGeo, useGlobalState, useResponsive } from '@/hooks';
import { ResponsiveCollection } from '@/interfaces';
import { API_CONFIG } from '@/modules/pmt/constants/affiliate.constants';
import { useAffiliateProducts } from '@/modules/pmt/hooks/useAffiliateProducts.hook';
import { useAffiliateSectionTracking } from '@/modules/pmt/hooks/useAffiliateTracking.hook';
import { AffiliateBadgeType, type AffiliateProduct } from '@/modules/pmt/interfaces';
import VerifiedIcon from '@/public/images/icons/verified.svg';
import { AffiliateTrackingService } from '@/services';
import { gtmAffiliateProductClick } from '@/utils/GTM';
import { type FC, useEffect, useRef } from 'react';
import { getAmazonRegionFromCountry } from '../../utils';
import { AffiliateSectionContainer } from '../TrackingDetailsLayout/TrackingDetailsLayout.styles';
import {
  Description,
  HeaderSection,
  ProductsContainer,
  SectionTitle,
  StyledSectionContainer,
  VerifiedContent,
  VerifiedPartnerBadge,
} from './AffiliateSection.styles';
import ProductCard from './ProductCard';

/**
 * Tracking Systems:
 *
 * User views AffiliateSection with 3 products
 *     ↓
 * Section becomes visible (50% in viewport)
 *     ↓
 * DUAL TRACKING FIRES:
 *     │
 *     ├─→ GTM System (Google Analytics)
 *     │   └─→ useAffiliateSectionTracking()
 *     │       └─→ pushEvent('view_affiliate_section')
 *     │           └─→ window.dataLayer → GTM → Google Analytics 4
 *     │
 *     └─→ API System (Affiliate Dashboard)
 *         ├─→ trackImpression(...)
 *         ├─→ trackImpression(...)
 *         └─→ trackImpression(...)
 *             └─→ AffiliateTrackingService → Event Queue (5s batch) → Backend API
 *
 * User clicks BUY NOW on Product
 *     ↓
 * DUAL TRACKING FIRES:
 *     │
 *     ├─→ GTM: handleClick() → pushEvent('affiliate_product_click')
 *     └─→ API: onClick() → trackClick(...)
 */

interface AffiliateSectionProps {
  shipmentId?: string;
}

const AffiliateSection: FC<AffiliateSectionProps> = ({ shipmentId }) => {
  const { t } = useGlobalState();
  const { geoInfo } = useGeo();

  const { isDesktopPMT } = useResponsive([ResponsiveCollection.DesktopPMT]);

  const trackingServiceRef = useRef<AffiliateTrackingService | null>(null);

  const locale = getAmazonRegionFromCountry(geoInfo.country);

  useEffect(() => {
    trackingServiceRef.current = new AffiliateTrackingService();

    return () => {
      trackingServiceRef.current?.destroy();
    };
  }, []);

  const { affiliateProducts, isLoading } = useAffiliateProducts({
    shipmentId: shipmentId || '',
  });

  const { sectionRef } = useAffiliateSectionTracking({
    onImpression: () => {
      // API Tracking: Track impression for EACH product displayed
      if (affiliateProducts && affiliateProducts.length > 0) {
        affiliateProducts.forEach((product) => {
          trackingServiceRef.current?.trackImpression({
            productId: product.id,
            url: product.affiliateUrl,
            locale,
            mainCategory: product.mainCategory,
          });
        });
      }
    },
    productIds: affiliateProducts?.map((p) => p.id),
  });

  const badgeLabels: Record<AffiliateBadgeType, string> = {
    [AffiliateBadgeType.AmazonChoice]: "Amazon's Choice",
    [AffiliateBadgeType.Prime]: 'Prime',
    [AffiliateBadgeType.Coupon]: 'Coupon',
    [AffiliateBadgeType.LimitedTimeDeal]: 'Limited Time Deal',
  };

  const handleClickBuyNow = (product: AffiliateProduct) => {
    // API Tracking
    trackingServiceRef.current?.trackClick({
      productId: product.id,
      url: product.affiliateUrl,
      locale,
      mainCategory: product.mainCategory,
    });

    // GTM Tracking
    try {
      gtmAffiliateProductClick({
        productAsin: product.asin,
        productTitle: product.title,
        linkUrl: product.affiliateUrl,
        productBadge: badgeLabels[product.badge as AffiliateBadgeType],
      });
    } catch (error) {
      console.error(error);
    }

    window.open(product.affiliateUrl, '_blank', 'noopener,noreferrer');
  };

  if (isLoading || !shipmentId || !affiliateProducts || affiliateProducts?.length < API_CONFIG.MIN_PRODUCTS_TO_SHOW) {
    return null;
  }

  const sectionUI = (
    <StyledSectionContainer ref={sectionRef}>
      <HeaderSection>
        <VerifiedPartnerBadge>
          <VerifiedIcon />
          <VerifiedContent>{t(MasterTranslation.AffiliateSectionVerifiedPartner)}</VerifiedContent>
        </VerifiedPartnerBadge>

        <SectionTitle>
          <Typography variant={TextStyles['Heading 2']}>{t(MasterTranslation.AffiliateSectionTitle)}</Typography>
        </SectionTitle>

        <Description>{t(MasterTranslation.AffiliateSectionDescription)}</Description>
      </HeaderSection>

      <ProductsContainer>
        {affiliateProducts.map((product: AffiliateProduct) => (
          <ProductCard key={product.id} product={product} onClick={() => handleClickBuyNow(product)} />
        ))}
      </ProductsContainer>
    </StyledSectionContainer>
  );

  return isDesktopPMT ? <AffiliateSectionContainer>{sectionUI}</AffiliateSectionContainer> : sectionUI;
};

export default AffiliateSection;
